import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserContext } from '../context/UserContext';
import { checkAndConsumeUrlToken, exchangeTicketAsync, hasPendingExchangeTicket, getToken } from '../utils/auth';
import Footer from '../components/Footer';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const { refreshUser } = useContext(UserContext);
  const navigate = useNavigate();

  // Auto-login from URL exchange ticket or URL token or redirect if already logged in
  useEffect(() => {
    let cancelled = false;

    const handleAutoAuth = async () => {
      if (hasPendingExchangeTicket()) {
        setIsLoading(true);
        await exchangeTicketAsync();
      } else {
        checkAndConsumeUrlToken();
      }

      if (cancelled) return;

      const token = getToken();
      if (token || user) {
        if (refreshUser) {
          refreshUser().catch(() => {});
        }
        navigate('/dashboard', { replace: true });
      } else {
        setIsLoading(false);
      }
    };

    handleAutoAuth();

    return () => {
      cancelled = true;
    };
  }, [user, navigate, refreshUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await login(email, password);
      
      // Refresh UserContext to load subscription data
      if (refreshUser) {
        await refreshUser();
      }
      
      // Dispatch event to notify UserContext of token change
      window.dispatchEvent(new Event('tokenChanged'));
      
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get token for conditional rendering
  const token = localStorage.getItem('token');

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      {/* Header */}
      <header className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
        </Link>
        <Link
          to="/"
          className="text-sm text-white/70 hover:text-white transition-colors"
        >
          Back to home
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-20 py-12">
        <div className="max-w-md w-full">
          <div 
            className="bg-white/5 backdrop-blur rounded-3xl p-8 border border-white/10 shadow-2xl"
            style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
          >
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
              <p className="text-white/70">Sign in to your AdSmash account</p>
            </div>

            {error && (
              <div className="mb-6 rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                />
              </div>

              <div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-white/70 hover:text-white transition-colors">
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-adsmash-primary w-full inline-flex items-center justify-center rounded-lg px-6 py-4 text-white font-semibold shadow-lg transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/* Purchase Link - Always visible for users to purchase without account */}
            {!token && !user && (
              <div className="mt-6 text-center">
                <Link
                  to="/payment"
                  className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-white font-semibold shadow-lg transition hover:opacity-90 border border-white/20 hover:bg-white/10"
                >
                  Complete your Registration
                </Link>
              </div>
            )}

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Login; 